import type { MainsailWwwConfig } from "../config";
import type { RouterFactory } from "./navigation/navigation-service";
import { configure } from "../config";
import { makeRoutes } from "../routing/routes";
import { ApiService } from "./api/api-service";
import { AuthService } from "./auth/auth-service";
import { GangplankClient } from "./auth/gangplank-client";
import { BillingService } from "./billing/billing-service";
import { bootstrapQuery } from "./bootstrap.gql";
import { BrowserService } from "./browser/browser-service";
import { CrudService } from "./crud/crud-service";
import { CurrencyService } from "./currency/currency-service";
import { ErrorService } from "./error/error-service";
import { ImageService } from "./image/image-service";
import { MapsService } from "./maps/maps-service";
import { NavigationService } from "./navigation/navigation-service";
import { NotificationService } from "./notification/notification-service";
import { PromptService } from "./prompt/prompt-service";
import { ReferenceService } from "./reference/reference-service";
import { TranslationService } from "./translation/translation-service";


export type ServicesInit = {
	routerFactory: RouterFactory;
	config: MainsailWwwConfig;
};

type ServicesDependencies = {
	api: ApiService;
	auth: AuthService;
	reference: ReferenceService;
	translation: TranslationService;
	currency: CurrencyService;
	notification: NotificationService;
	maps: MapsService;
	prompt: PromptService;
	crud: CrudService;
	image: ImageService;
	navigation: NavigationService;
	billing: BillingService;
	error: ErrorService;
};
export class Services {
	readonly #api: ApiService;
	readonly #auth: AuthService;
	readonly #reference: ReferenceService;
	readonly #translation: TranslationService;
	readonly #currency: CurrencyService;
	readonly #notification: NotificationService;
	readonly #maps: MapsService;
	readonly #prompt: PromptService;
	readonly #crud: CrudService;
	readonly #image: ImageService;
	readonly #navigation: NavigationService;
	readonly #billing: BillingService;
	readonly #error: ErrorService;

	private constructor(deps: Readonly<ServicesDependencies>) {
		this.#api = deps.api;
		this.#auth = deps.auth;
		this.#reference = deps.reference;
		this.#translation = deps.translation;
		this.#currency = deps.currency;
		this.#notification = deps.notification;
		this.#maps = deps.maps;
		this.#prompt = deps.prompt;
		this.#crud = deps.crud;
		this.#image = deps.image;
		this.#navigation = deps.navigation;
		this.#billing = deps.billing;
		this.#error = deps.error;
	}

	static async initialise(init: ServicesInit): Promise<Services> {
		const config = configure();
		const gangplank = new GangplankClient(config.gangplankUrl);

		const browser = new BrowserService({
			cookieProvider: document,
			localStorageProvider: localStorage,
			refreshHandler: window.location.reload.bind(window.location),
		});
		const api = new ApiService({
			uri: config.apiUrl,
		}, {
			gangplank,
		});

		const translation = new TranslationService();
		const notification = new NotificationService({ translation });

		const auth = new AuthService({
			api,
			browser,
			gangplank,
			notification,
		 });

		const prompt = new PromptService();
		const error = new ErrorService({ api, auth, prompt });

		const bootstrap = await api.queryOnce(bootstrapQuery);
		const reference = new ReferenceService(bootstrap);

		browser["initialise"](reference.setting("cookieDomain"));
		auth["initialise"](bootstrap.activeUser);

		await translation["initialise"](bootstrap.translations);

		const currency = new CurrencyService(bootstrap.currencies);

		const maps = new MapsService({ apiKey: config.googleMapKey });
		const crud = new CrudService();
		const image = new ImageService(config);

		const navigation = new NavigationService({
			routerFactory: init.routerFactory,
			routesFactory: makeRoutes,
		});

		const billing = new BillingService(bootstrap.billingSettings);

		const services = new Services({
			api,
			auth,
			reference,
			translation,
			currency,
			notification,
			maps,
			prompt,
			crud,
			image,
			navigation,
			billing,
			error,
		});

		prompt["initialise"](services);
		crud["initialise"](services);
		navigation["initialise"](services);

		return services;
	}

	get api(): ApiService {
		return this.#api;
	}

	get auth(): AuthService {
		return this.#auth;
	}

	get reference(): ReferenceService {
		return this.#reference;
	}

	get translation(): TranslationService {
		return this.#translation;
	}

	get currency(): CurrencyService {
		return this.#currency;
	}

	get notification(): NotificationService {
		return this.#notification;
	}

	get maps(): MapsService {
		return this.#maps;
	}

	get prompt(): PromptService {
		return this.#prompt;
	}

	get crud(): CrudService {
		return this.#crud;
	}

	get image(): ImageService {
		return this.#image;
	}

	get navigation(): NavigationService {
		return this.#navigation;
	}

	get billing(): BillingService {
		return this.#billing;
	}

	get error(): ErrorService {
		return this.#error;
	}
}
